import { useContext, useEffect, useState } from 'react';
import { Divider } from 'primereact/divider';
import { useDebounce } from 'primereact/hooks';
import { useWindowSize } from 'react-use';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AlertVariant, MOBILE_BREAKPOINT, OrgPermissionKeys, OrgSubPermissionKeys } from '../../../../constants/constants';
import { _ToastMessageProps, _VenuePickerOption } from '../../../../constants/staticTypes';
import MlxSearch from '../../../../components/common/MlxSearch/MlxSearch';
import withSidebar from '../../../../hoc/withSideBar/withSideBar';
import logger from '../../../../utils/logger';
import { getVenueOptions } from '../../../../helpers/venueHelper';
import { COPY_MENU_ITEMS_FROM, COPY_MENU_ITEMS_TO, MENU_LIST, MENU_MANAGEMENT, SELECT_VENUE } from '../../../../constants/strings';
import { useTranslation } from '../../../../hooks/useTranslation';
import { VENUE_LIST_MENU } from '../../../../constants/queryKeys';
import MlxDropDown from '../../../../components/common/MlxDropDown/MlxDropDown';
import { TreeNode } from 'primereact/treenode';
import { TreeTable, TreeTableSelectionKeysType } from 'primereact/treetable';
import { Column } from 'primereact/column';
import MlxBlockUI from '../../../../components/common/MlxBlockUI/MlxBlockUI';
import { Checkbox } from 'primereact/checkbox';
import MlxButton from '../../../../components/common/MlxButton/MlxButton';
import {
    _BulkCopyPayloadType,
    buildMenuPayload,
    bulkCopyMenuItems,
    getVenueFbMenu,
    transformMenuDataToTreeTable,
} from '../../../../helpers/venueMenuHelper';
import MlxConfirmPopup from '../../../../components/common/MlxConfirmPopup/MlxConfirmPopup';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import HttpServiceHelper from '../../../../helpers/httpServiceHelper';
import { defaultToastData } from '../../../../helpers/commonHelper';
import MlxToast from '../../../../components/common/MlxToast/MlxToast';
import CopyFromVenueModal from './components/CopyFromVenueModal';
import MlxToolTip from '../../../../components/common/MlxToolTip/MlxToolTip';
import { checkActionSubPermission } from '../../../../utils/common';
import { AuthCtx } from '../../../../context/AuthCtxProvider';

const languageTexts = {
    MENU_MANAGEMENT: MENU_MANAGEMENT,
    MENU_LIST: MENU_LIST,
    SELECT_VENUE: SELECT_VENUE,
    COPY_MENU_ITEMS_TO: COPY_MENU_ITEMS_TO,
    COPY_MENU_ITEMS_FROM: COPY_MENU_ITEMS_FROM,
};

function VenueMenuManagement() {
    const { width: screenWidth } = useWindowSize();
    const [searchValue, debouncedSearchValue, setSearchValue] = useDebounce('', 0);
    const queryClient = useQueryClient();
    const translatedText = useTranslation(languageTexts, 'none');
    const menuBulkCopyMutation = useMutation(HttpServiceHelper);
    const { userType } = useContext(AuthCtx);
    const location = useLocation();
    const requestedVenueId = (location.state as { venueId?: string } | null)?.venueId;

    const [searchBarVisible, setSearchBarVisible] = useState(screenWidth > MOBILE_BREAKPOINT);
    const [isVenueListLoading, setIsVenueListLoading] = useState<boolean>(false);
    const [venueOptions, setVenueOptions] = useState<_VenuePickerOption[]>([]);
    const [selectedVenueOption, setSelectedVenueOption] = useState<string>();
    const [nodes, setNodes] = useState<TreeNode[]>([]);
    const [selectedNodeKeys, setSelectedNodeKeys] = useState<TreeTableSelectionKeysType>({});
    const [expandedKeys, setExpandedKeys] = useState<{ [key: string]: boolean }>({});
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [selectedCopyVenueOption, setSelectedCopyVenueOption] = useState<string>();
    const [copyPopupVisible, setCopyPopupVisible] = useState(false);
    const [popupTarget, setPopupTarget] = useState<any>(null);
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);
    const [isCopyInProgress, setIsCopyInProgress] = useState(false);
    const [copyType, setCopyType] = useState<'copy-from' | 'copy-to' | ''>('');
    const [copyFromVenueModalVisible, setCopyFromVenueModalVisible] = useState(false);

    useEffect(() => {
        setIsVenueListLoading(true);
        const getVenues = async () => {
            const venueOptionsList = await getVenueOptions(queryClient);
            setVenueOptions(venueOptionsList);
            if (venueOptionsList?.length > 0) {
                const requested = venueOptionsList.find((option) => option.id === requestedVenueId);
                setSelectedVenueOption((requested ?? venueOptionsList[0]).id);
            }
            setIsVenueListLoading(false);
        };
        getVenues();
    }, []);

    const venueFbMenuQuery = useQuery({
        queryKey: VENUE_LIST_MENU,
        queryFn: () => getVenueFbMenu(queryClient, selectedVenueOption),
        onSuccess: (response) => {
            const responseData = response?.data?.data;
            const transformedData = transformMenuDataToTreeTable(responseData); // get the data in the required tree format
            setNodes(transformedData);
        },
        onError: (error) => logger.log('Failed to fetch admin staff list', error),
        refetchOnWindowFocus: false,
        enabled: false,
    });

    useEffect(() => {
        if (!selectedVenueOption) return;
        venueFbMenuQuery.refetch();
    }, [selectedVenueOption]);

    function findAndExpandMatchingNodes(
        nodes: TreeNode[],
        searchTerm: string
    ): { matchedKeys: string[]; expanded: { [key: string]: boolean } } {
        const matchedKeys: string[] = [];
        const expanded: { [key: string]: boolean } = {};

        function search(nodeList: TreeNode[], parentKeyPath: string[] = []) {
            for (const node of nodeList) {
                const label = String(node.label ?? '').toLowerCase();
                const matches = label.includes(searchTerm.toLowerCase());

                const currentPath = [...parentKeyPath];

                if (matches) {
                    matchedKeys.push(node.key as string);
                    // Expand all ancestors
                    for (const key of currentPath) {
                        expanded[key] = true;
                    }
                }

                if (node.children?.length) {
                    search(node.children, [...currentPath, node.key as string]);
                }
            }
        }

        search(nodes);
        return { matchedKeys, expanded };
    }

    useEffect(() => {
        if (!debouncedSearchValue?.trim()) {
            setExpandedKeys({}); // Collapse all if search is empty
            return;
        }

        const { expanded } = findAndExpandMatchingNodes(nodes, debouncedSearchValue);

        setExpandedKeys(expanded);
    }, [debouncedSearchValue, nodes]);

    const getAllNodeKeys = (nodes: TreeNode[]): TreeTableSelectionKeysType => {
        const keys: TreeTableSelectionKeysType = {};

        const traverse = (nodeList: TreeNode[]) => {
            for (const node of nodeList) {
                keys[node.key as string] = { checked: true, partialChecked: false };
                if (node.children) {
                    traverse(node.children);
                }
            }
        };

        traverse(nodes);
        return keys;
    };

    const toggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedNodeKeys({});
        } else {
            const allKeys = getAllNodeKeys(nodes);
            setSelectedNodeKeys(allKeys);
        }
        setIsAllSelected((prev) => !prev);
    };

    const handleBulkCopy = () => {
        const menuPayload = buildMenuPayload(nodes, selectedNodeKeys);

        const copyMenuPayload: _BulkCopyPayloadType = {
            tenantId: (copyType === 'copy-to' ? selectedVenueOption : selectedCopyVenueOption) ?? '',
            newTenantId: (copyType === 'copy-to' ? selectedCopyVenueOption : selectedVenueOption) ?? '',
            menus: menuPayload.menus,
        };

        setIsCopyInProgress(true);

        bulkCopyMenuItems(
            menuBulkCopyMutation,
            copyMenuPayload,
            (data: any) => {
                setToastMessage({
                    message: data?.message ?? '',
                    variant: AlertVariant.SUCCESS,
                    show: true,
                });
                setIsCopyInProgress(false);
                setSelectedNodeKeys({});
            },
            (error: string, variant: string) => {
                setToastMessage({
                    message: error,
                    variant,
                    show: true,
                });
                setIsCopyInProgress(false);
            }
        );
    };

    const confirmCopyVenue = (event: any) => {
        setPopupTarget(event.currentTarget);
        setCopyPopupVisible(true);
    };

    const onCopyFromSuccess = () => {
        venueFbMenuQuery.refetch();
        setSelectedCopyVenueOption('');
        setCopyFromVenueModalVisible(false);
    };

    return (
        <MlxBlockUI isLoading={venueFbMenuQuery.isFetching || isVenueListLoading || isCopyInProgress} opacity={0.5}>
            {copyFromVenueModalVisible && (
                <CopyFromVenueModal
                    visible={true}
                    setVisible={setCopyFromVenueModalVisible}
                    selectedVenueOption={selectedVenueOption}
                    selectedCopyVenueOption={selectedCopyVenueOption}
                    onCopyFromSuccess={onCopyFromSuccess}
                    venueOptions={venueOptions}
                    setToastMessage={setToastMessage}
                />
            )}
            <MlxToast
                data-testid="toast"
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            <ConfirmPopup
                target={popupTarget}
                visible={copyPopupVisible}
                onHide={() => {
                    if (copyType === 'copy-to') setSelectedCopyVenueOption('');
                    setCopyPopupVisible(false);
                }}
                message={
                    <>
                        <div className="mb-2">
                            {copyType === 'copy-to' ? translatedText.COPY_MENU_ITEMS_TO : translatedText.COPY_MENU_ITEMS_FROM}
                        </div>
                        <MlxDropDown
                            dropDownSize="large"
                            options={venueOptions?.filter((item) => item.id !== selectedVenueOption && item.isActive)}
                            optionLabel="venueName"
                            optionValue="id"
                            value={selectedCopyVenueOption}
                            placeholder={translatedText.SELECT_VENUE}
                            onChange={(e) => {
                                e.preventDefault();
                                setSelectedCopyVenueOption(e.value);
                            }}
                            style={{ width: '200px' }}
                        />
                    </>
                }
                acceptLabel="Proceed"
                rejectLabel="Cancel"
                accept={() => {
                    if (copyType === 'copy-to') {
                        handleBulkCopy();
                        setCopyPopupVisible(false);
                    } else {
                        setCopyFromVenueModalVisible(true);
                    }
                }}
                reject={() => {
                    setSelectedCopyVenueOption('');
                    setCopyPopupVisible(false);
                }}
                acceptClassName={`p-button ${!selectedCopyVenueOption ? 'p-disabled' : ''}`}
            />

            <div className="page-section h-100">
                <span className="page-header d-flex justify-content-between">
                    <span>{translatedText.MENU_MANAGEMENT}</span>
                    <MlxDropDown
                        dropDownSize="large"
                        options={venueOptions}
                        optionLabel="venueName"
                        optionValue="id"
                        value={selectedVenueOption}
                        placeholder={translatedText.SELECT_VENUE}
                        onChange={(e) => {
                            setSelectedVenueOption(e.value);
                            setSelectedNodeKeys({});
                        }}
                        style={{ width: '200px' }}
                        disabled={venueOptions?.length <= 1}
                    />
                </span>
                <div className="page-content h-100">
                    <div className="content-header flex-grow-0">
                        <span className="header-title">{searchBarVisible ? translatedText.MENU_LIST : ''}</span>
                        <div className="header-fields">
                            {(userType === 'organizationAdmin' ||
                                checkActionSubPermission(
                                    queryClient,
                                    OrgPermissionKeys.MANAGE_VENUE_MENU,
                                    OrgSubPermissionKeys.ADD_VENUE_MENU
                                )) && (
                                <>
                                    <MlxToolTip target={'.copyFromButton'}>
                                        <MlxButton
                                            className="bulk-copy-button copyFromButton"
                                            outlined
                                            onClick={(e) => {
                                                setCopyType('copy-from');
                                                confirmCopyVenue(e);
                                            }}
                                            data-pr-tooltip="Copy menu items from another venue"
                                            data-pr-position="bottom"
                                        >
                                            <i className="pi pi-file-import"></i>
                                        </MlxButton>
                                    </MlxToolTip>
                                    <MlxToolTip target={'.copyToButton'}>
                                        <MlxButton
                                            className="bulk-copy-button copyToButton"
                                            outlined
                                            onClick={(e) => {
                                                setCopyType('copy-to');
                                                confirmCopyVenue(e);
                                            }}
                                            disabled={!Object.keys(selectedNodeKeys).length}
                                            data-pr-tooltip="Copy menu items to another venue"
                                            data-pr-position="bottom"
                                        >
                                            <i className="pi pi-file-export"></i>
                                        </MlxButton>
                                    </MlxToolTip>
                                </>
                            )}
                            <MlxSearch
                                className="ms-2 p-inputtext-sm"
                                removeLabelOnMobile={true}
                                placeholder="Search"
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                                searchBarVisible={setSearchBarVisible}
                                isLoading={venueFbMenuQuery?.isFetching && !!debouncedSearchValue.trim().length}
                            />
                        </div>
                    </div>
                    <Divider className="content-divider" />
                    <div className="content-body flex-grow-1">
                        <TreeTable
                            className="custom-p-treetable"
                            value={nodes}
                            selectionMode="checkbox"
                            selectionKeys={selectedNodeKeys}
                            onSelectionChange={(e) => setSelectedNodeKeys(e.value as TreeTableSelectionKeysType)}
                            expandedKeys={expandedKeys}
                            onToggle={(e) => setExpandedKeys(e.value)}
                            tableStyle={{ minWidth: '50rem' }}
                            scrollable
                            pt={{
                                scrollableBody: {
                                    style: {
                                        maxHeight: 'calc(100vh - 20rem)',
                                    },
                                },
                            }}
                        >
                            <Column
                                header={
                                    <span>
                                        <Checkbox
                                            checked={isAllSelected}
                                            onChange={toggleSelectAll}
                                            style={{ marginRight: '0.5rem' }}
                                        ></Checkbox>
                                        Menu Name
                                    </span>
                                }
                                expander
                                body={(node) => {
                                    const label = node.label ?? '';
                                    if (debouncedSearchValue) {
                                        const regex = new RegExp(`(${debouncedSearchValue})`, 'ig');
                                        const parts = label.split(regex);
                                        return (
                                            <>
                                                {parts.map((part: any, i: any) =>
                                                    part.toLowerCase() === debouncedSearchValue.toLowerCase() ? (
                                                        <span key={i} style={{ backgroundColor: 'yellow' }}>
                                                            {part}
                                                        </span>
                                                    ) : (
                                                        <span key={i}>{part}</span>
                                                    )
                                                )}
                                            </>
                                        );
                                    }
                                    return label;
                                }}
                            />
                            <Column
                                header="Type"
                                body={(node) => {
                                    const type = node.data?.type;
                                    return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
                                }}
                            />
                            <Column header="Price" body={(node) => node.data?.itemPrices ?? '-'} />
                        </TreeTable>
                    </div>
                </div>
            </div>
        </MlxBlockUI>
    );
}

export default withSidebar(VenueMenuManagement);
